import { Button as MUIButton, ButtonProps as MUIButtonProps, styled, Theme } from '@mui/material';
import clsx from 'clsx';

type ButtonAppearance = 'primary' | 'secondary' | 'neutral';
type ExtraProps = { fullRounded?: boolean; appearance?: ButtonAppearance };

type ButtonProps = MUIButtonProps &
    ExtraProps &
    Partial<Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof MUIButtonProps>>;

const getButtonSizeStyles = (size: NonNullable<MUIButtonProps['size']>) => {
    const verticalPadding = {
        small: '4px',
        medium: '6px',
        large: '8px',
        xl: '12px',
    }[size];

    return {
        paddingTop: verticalPadding,
        paddingBottom: verticalPadding,
    };
};

const mergeButtonSx = (sx: ButtonProps['sx'], size: NonNullable<MUIButtonProps['size']>) => {
    const sizeStyles = getButtonSizeStyles(size);

    if (sx == null) {
        return sizeStyles;
    }

    return Array.isArray(sx) ? [...sx, sizeStyles] : [sx, sizeStyles];
};

const getCommonStyles = ({ theme, fullRounded }: { theme: Theme; fullRounded?: boolean }) => {
    return {
        borderRadius: fullRounded ? 9999 : theme.shape.borderRadius,
        fontSize: 14,
        letterSpacing: 0,
        fontWeight: 400,
    };
};

const ButtonComponent = styled(
    ({ fullRounded, appearance = 'primary', variant, color, size, className, ...props }: ButtonProps) => {
        const appearanceProps = {
            primary: { variant: 'contained' as const, color: 'primary' as const },
            secondary: { variant: 'outlined' as const, color: 'secondary' as const },
            neutral: { variant: 'outlined' as const, color: 'neutral-800' as const, size: 'small' as const },
        }[appearance];
        const buttonSize = size ?? appearanceProps.size;

        return (
            <MUIButton
                {...props}
                variant={variant ?? appearanceProps.variant}
                color={color ?? appearanceProps.color}
                size={buttonSize}
                sx={mergeButtonSx(props.sx, buttonSize ?? 'medium')}
                className={clsx(className, 'black-border')}
            />
        );
    }
)(({ fullRounded, appearance, ...props }) => ({
    ...getCommonStyles({ ...props, fullRounded }),
    ...(appearance === 'neutral' && {
        borderColor: props.theme.palette['neutral-450'].main,
    }),
}));

const Primary = (props: ButtonProps) => <ButtonComponent {...props} appearance="primary" />;

const Secondary = (props: ButtonProps) => <ButtonComponent {...props} appearance="secondary" />;

const Neutral = ({ fullRounded = true, ...props }: ButtonProps) => (
    <ButtonComponent {...props} appearance="neutral" fullRounded={fullRounded} />
);

const ButtonNamespace = Object.assign(ButtonComponent, {
    Primary,
    Secondary,
    Neutral,
});

export { ButtonNamespace as Button };
export type { ButtonAppearance, ButtonProps };
